import { ChevronDown, ChevronRight, Edit, Eye, Trash } from 'lucide-react';
import { useState } from 'react';
import { useCountryById } from '../hooks/useCountryById';
import { useCountryHierarchy } from '../hooks/useCountryHierarchy';
import { useToggleCountryStatus } from '../hooks/useToggleCountryStatus';
import {
  TerritorialLevel,
  type CountryHierarchy,
  type TerritorialLevel as TerritorialLevelType,
} from '../types/countries.types';
import { DeleteCountryModal } from './DeleteCountryModal';
import { EditCountryModal } from './EditCountryModal';
import Protected from '@/shared/components/ahkinia/Protected/Protected';
import { Switch } from '@/shared/components/ahkinia/Switch/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { PERMISSIONS } from '@/shared/constants/permissions';

interface CountryDetailModalProps {
  countryId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function getLevelLabel(level: TerritorialLevelType): string {
  switch (level) {
    case TerritorialLevel.Pais:
      return 'País';
    case TerritorialLevel.Estado:
      return 'Estado';
    case TerritorialLevel.Municipio:
      return 'Municipio';
    case TerritorialLevel.Localidad:
      return 'Localidad';
    default:
      return String(level);
  }
}

interface HierarchyItemProps {
  item: CountryHierarchy;
  level: number;
  onToggle: (item: CountryHierarchy, isActive: boolean) => void;
  isTogglingId: string | null;
}

function HierarchyItem({ item, level, onToggle, isTogglingId }: HierarchyItemProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const isToggling = isTogglingId === item.id;

  return (
    <div
      data-testid={`hierarchy-item-${item.id}`}
      className={`${level > 0 ? 'ml-4 border-l border-outline pl-3' : ''}`}
    >
      <div className="flex items-center gap-2 py-1">
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-0.5 hover:bg-outline rounded cursor-pointer"
          >
            {isExpanded ? (
              <ChevronDown className="size-4 text-primary/60" />
            ) : (
              <ChevronRight className="size-4 text-primary/60" />
            )}
          </button>
        ) : (
          <span className="w-5" />
        )}
        <span className={`text-primary font-medium ${!item.isActive ? 'opacity-50' : ''}`}>
          {item.officialName}
        </span>
        <span className="text-xs text-primary/60 bg-outline px-1.5 py-0.5 rounded">
          {getLevelLabel(item.level)}
        </span>
        <div className="ml-auto flex flex-row">
          <Protected permissions={PERMISSIONS.COUNTRIES_VIEW}>
            <UnifiedButton icon={<Eye />} onClick={() => setDetailModalOpen(true)}></UnifiedButton>
          </Protected>
          <Protected permissions={PERMISSIONS.COUNTRIES_EDIT}>
            <UnifiedButton icon={<Edit />} onClick={() => setEditModalOpen(true)}></UnifiedButton>
          </Protected>
          <Protected permissions={PERMISSIONS.COUNTRIES_TOGGLE}>
            <Switch
              checked={item.isActive}
              onCheckedChange={(checked) => onToggle(item, checked)}
              disabled={isToggling}
            />
          </Protected>
          <Protected permissions={PERMISSIONS.COUNTRIES_DELETE}>
            {!item.isActive ? (
              <UnifiedButton
                disabled={item.isActive}
                icon={<Trash />}
                onClick={() => setDeleteModalOpen(true)}
              ></UnifiedButton>
            ) : (
              <UnifiedButton
                icon={<Trash />}
                title="Debe deshabilitarse antes de eliminar"
                className="text-muted-foreground cursor-not-allowed opacity-50"
                disabled
              ></UnifiedButton>
            )}
          </Protected>
        </div>
      </div>
      {hasChildren && isExpanded && (
        <div className="mt-1">
          {item.children.map((child) => (
            <HierarchyItem
              key={child.id}
              item={child}
              level={level + 1}
              onToggle={onToggle}
              isTogglingId={isTogglingId}
            />
          ))}
        </div>
      )}
      <CountryDetailModal
        countryId={item.id}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
      />
      <DeleteCountryModal
        countryId={item.id}
        countryName={item.officialName}
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
      />
      <EditCountryModal
        disableChild
        countryId={item.id}
        open={editModalOpen}
        onOpenChange={() => {
          setEditModalOpen(false);
        }}
      />
    </div>
  );
}

export function CountryDetailModal({ countryId, open, onOpenChange }: CountryDetailModalProps) {
  const { country } = useCountryById(countryId);
  const { hierarchy } = useCountryHierarchy(countryId, open);
  const { toggleStatus, isToggling } = useToggleCountryStatus();
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleToggle = (item: CountryHierarchy, isActive: boolean) => {
    setTogglingId(item.id);
    toggleStatus(
      { id: item.id, isActive, cascade: 'enable', subtree: item },
      { onSettled: () => setTogglingId(null) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="country-detail-modal"
        className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>Detalle del país</DialogTitle>
        </DialogHeader>

        {country && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">
                  Nombre oficial
                </label>
                <p data-testid="detail-country-official-name" className="text-primary font-medium">
                  {country.officialName}
                </p>
              </div>
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">
                  Nombre común / alias
                </label>
                <p data-testid="detail-country-common-name" className="text-primary">
                  {country.commonName}
                </p>
              </div>
              {country.level === TerritorialLevel.Pais && (
                <div>
                  <label className="text-xs text-primary/60 uppercase tracking-wide">
                    Código ISO
                  </label>
                  <p data-testid="detail-country-isocode" className="text-primary font-mono">
                    {country.isoCode}
                  </p>
                </div>
              )}
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">
                  Moneda base
                </label>
                <p data-testid="detail-country-currency" className="text-primary">
                  {country.currency}
                </p>
              </div>
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">Estado</label>
                <div className="mt-1">
                  <span data-testid="detail-country-status">
                    <StatusBadge
                      status={country.isActive ? 'active' : 'inactive'}
                      label={country.isActive ? 'Habilitado' : 'Deshabilitado'}
                    />
                  </span>
                </div>
              </div>
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">
                  Fecha de creación
                </label>
                <p data-testid="detail-country-created-at" className="text-primary text-sm">
                  {formatDate(country.createdAt)}
                </p>
              </div>
            </div>

            {hierarchy && hierarchy.children && hierarchy.children.length > 0 && (
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide mb-3 block">
                  Jerarquía territorial
                </label>
                <div
                  data-testid="country-hierarchy"
                  className="border border-outline rounded-lg p-3 bg-background"
                >
                  {hierarchy.children.map((child) => (
                    <HierarchyItem
                      key={child.id}
                      item={child}
                      level={0}
                      onToggle={handleToggle}
                      isTogglingId={isToggling ? togglingId : null}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end pt-4">
          <UnifiedButton
            data-testid="close-detail-modal-btn"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cerrar
          </UnifiedButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
